import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, Send } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SpellcheckTextarea from "@/components/SpellcheckTextarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import BuyerProfileSummary from "@/components/BuyerProfileSummary";
import LicenseInvoiceSection from "@/components/LicenseInvoiceSection";
import LicensePaymentProofSection from "@/components/LicensePaymentProofSection";
import {
  buildAgreementAcceptancePayload,
  buildLicenseRequestFormDefaults,
  buildLicenseRequestPayload,
  canManageCommercialLicenseRequests,
  commercialPlanLabel,
  findLatestApprovedRequest,
  formatRequestedModulesLabel,
  formatRequiresInvoiceLabel,
  hasActiveCommercialLicense,
  hasActiveRequest,
  hasAtLeastOneRequestedModule,
  hasBuyerProfileBasics,
  isSubmissionBlocked,
  LICENSE_REQUEST_MSG_ACTIVE,
  LICENSE_REQUEST_MSG_APPROVED,
  LICENSE_REQUEST_MSG_LEGAL_LOAD_FAILED,
  LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED,
  LICENSE_REQUEST_MSG_NO_MODULE,
  LICENSE_REQUEST_MSG_RFC_REQUIRED,
  LICENSE_REQUEST_MSG_SUCCESS,
  licenseRequestStatusLabel,
  moduleIdsForLicenseRequest,
  moduleLabel,
  parseLicenseRequestSubmitError,
  resolveActiveModuleIds,
  resolveCommercialModuleCatalog,
  resolveRequestableModuleIds,
  validateInvoiceFields,
} from "@/lib/commercialLicenseRequests";
import { fetchSystemCommercialPlans } from "@/lib/commercialLicenseRequestsApi";
import { validateScopeFields } from "@/lib/commercialLicenseScope";
import {
  DEFAULT_INVOICE_CFDI_USE,
  DEFAULT_INVOICE_PAYMENT_METHOD,
} from "@/lib/commercialInvoiceCatalog";
import {
  defaultRequestedLicenseType,
  licenseTypeLabel,
  requestedLicenseTypeOptions,
} from "@/lib/licenseTypes";
import {
  createLicenseRequest,
  listMyLicenseRequests,
} from "@/lib/commercialLicenseRequestsApi";
import {
  createAgreementAcceptance,
  getCurrentLegalDocuments,
} from "@/lib/commercialAgreementAcceptancesApi";
import LegalAcceptanceCheckbox from "@/components/LegalAcceptanceCheckbox";
import LicenseRequestHistory from "@/components/LicenseRequestHistory";
import { toast } from "sonner";

function formatDateShort(iso) {
  if (!iso) return "—";
  return String(iso).slice(0, 10);
}

function SectionBlock({ title, children, testId }) {
  return (
    <section className="rounded-sm border border-slate-200 bg-slate-50/40 p-3 space-y-2" data-testid={testId}>
      <h5 className="text-xs font-semibold text-[#0F2D5C] m-0">{title}</h5>
      {children}
    </section>
  );
}

/**
 * Formulario + historial — solicitud web de licencia comprador → BNT.
 */
export default function LicenseRequestPanel({
  systemInfo,
  profileDefaults = null,
  showActiveModulesNotice = true,
  showSectionHeader = true,
  showHistory = true,
  submissionGate = null,
  onSubmissionGateChange,
  onRequestSubmitted,
  form: externalForm,
  setForm: externalSetForm,
  omitSections = {},
  sharedLegal = null,
}) {
  const { user } = useAuth();
  const profile = profileDefaults || systemInfo?.profile || {};
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [rfcError, setRfcError] = useState("");
  const [lastSubmitted, setLastSubmitted] = useState(null);
  const [internalForm, setInternalForm] = useState(() =>
    buildLicenseRequestFormDefaults(systemInfo, profile),
  );
  const form = externalForm ?? internalForm;
  const setForm = externalSetForm ?? setInternalForm;
  const [internalLegalCatalog, setInternalLegalCatalog] = useState(null);
  const [internalLegalLoading, setInternalLegalLoading] = useState(true);
  const [internalLegalError, setInternalLegalError] = useState("");
  const [internalLegalAccepted, setInternalLegalAccepted] = useState(false);
  const [commercialPlans, setCommercialPlans] = useState([]);
  const useSharedLegal = Boolean(sharedLegal);
  const legalCatalog = useSharedLegal ? sharedLegal.legalCatalog : internalLegalCatalog;
  const legalLoading = useSharedLegal ? sharedLegal.legalLoading : internalLegalLoading;
  const legalError = useSharedLegal ? sharedLegal.legalError : internalLegalError;
  const legalAccepted = useSharedLegal ? sharedLegal.legalAccepted : internalLegalAccepted;
  const legalLocked = useSharedLegal ? sharedLegal.legalLocked : false;
  const canProceedWithRequest = useSharedLegal
    ? sharedLegal.canProceedWithRequest
    : legalAccepted;
  const agreementAcceptanceId = useSharedLegal ? sharedLegal.agreementAcceptanceId : "";
  const setLegalAccepted = useSharedLegal ? sharedLegal.setLegalAccepted : setInternalLegalAccepted;

  const canManage = canManageCommercialLicenseRequests(user);
  const activeBlocked = isSubmissionBlocked(submissionGate) || hasActiveRequest(requests);
  const latestApproved = findLatestApprovedRequest(requests);
  const hasActiveLicense = hasActiveCommercialLicense(systemInfo);
  const activeModuleIds = useMemo(() => resolveActiveModuleIds(systemInfo), [systemInfo]);
  const requestableModuleIds = useMemo(() => resolveRequestableModuleIds(systemInfo), [systemInfo]);
  const licenseTypeOptions = useMemo(
    () => requestedLicenseTypeOptions(hasActiveLicense),
    [hasActiveLicense],
  );
  const allModulesActive = hasActiveLicense && requestableModuleIds.length === 0;
  const profileBasicsReady = hasBuyerProfileBasics(profile) || hasBuyerProfileBasics(form);

  const catalog = useMemo(() => resolveCommercialModuleCatalog(systemInfo), [systemInfo]);
  const requestModuleIds = useMemo(() => moduleIdsForLicenseRequest(catalog), [catalog]);
  const activeModuleLabels = useMemo(() => {
    if (!showActiveModulesNotice) return [];
    return activeModuleIds.map((id) => moduleLabel(id, catalog));
  }, [activeModuleIds, catalog, showActiveModulesNotice]);

  const refreshRequests = useCallback(async () => {
    setLoadError("");
    try {
      const data = await listMyLicenseRequests();
      const items = data?.items || [];
      setRequests(items);
      onSubmissionGateChange?.(data?.submission_gate || null);
      return items;
    } catch {
      setLoadError("No pudimos cargar tu historial de solicitudes.");
      setRequests([]);
      return [];
    } finally {
      setLoading(false);
    }
  }, [onSubmissionGateChange]);

  useEffect(() => {
    if (!canManage) {
      setLoading(false);
      if (!useSharedLegal) setInternalLegalLoading(false);
      return;
    }
    refreshRequests();
  }, [canManage, refreshRequests, useSharedLegal]);

  useEffect(() => {
    if (!canManage || hasActiveLicense) return undefined;
    let cancelled = false;
    fetchSystemCommercialPlans()
      .then((data) => {
        if (!cancelled) setCommercialPlans(data?.items || []);
      })
      .catch(() => {
        if (!cancelled) setCommercialPlans([]);
      });
    return () => {
      cancelled = true;
    };
  }, [canManage, hasActiveLicense]);

  useEffect(() => {
    if (!canManage || useSharedLegal) return undefined;
    let cancelled = false;
    setInternalLegalLoading(true);
    setInternalLegalError("");
    getCurrentLegalDocuments("license_request")
      .then((data) => {
        if (!cancelled) {
          setInternalLegalCatalog(data);
          setInternalLegalAccepted(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setInternalLegalCatalog(null);
          setInternalLegalError(LICENSE_REQUEST_MSG_LEGAL_LOAD_FAILED);
        }
      })
      .finally(() => {
        if (!cancelled) setInternalLegalLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canManage, useSharedLegal]);

  useEffect(() => {
    if (externalForm != null) return;
    setInternalForm(buildLicenseRequestFormDefaults(systemInfo, profile));
  }, [systemInfo, profileDefaults, externalForm]);

  const applyRegistrationInvoiceData = (checked) => {
    setForm((prev) => ({
      ...prev,
      use_registration_invoice_data: checked,
      invoice_rfc: checked ? (profile.rfc || prev.rfc || "").trim().toUpperCase() : prev.invoice_rfc,
      invoice_razon_social: checked
        ? (profile.razon_social || prev.razon_social || "").trim()
        : prev.invoice_razon_social,
      invoice_email: checked
        ? (profile.correo_facturacion || profile.email || prev.email || "").trim()
        : prev.invoice_email,
      invoice_codigo_postal: checked
        ? (profile.codigo_postal || prev.invoice_codigo_postal || "").trim()
        : prev.invoice_codigo_postal,
      invoice_fiscal_address: checked
        ? (profile.domicilio || prev.invoice_fiscal_address || "").trim()
        : prev.invoice_fiscal_address,
      invoice_regimen_fiscal: checked
        ? (profile.regimen_fiscal || prev.invoice_regimen_fiscal || "").trim()
        : prev.invoice_regimen_fiscal,
      invoice_cfdi_use: checked ? DEFAULT_INVOICE_CFDI_USE : prev.invoice_cfdi_use,
      invoice_payment_method: checked ? DEFAULT_INVOICE_PAYMENT_METHOD : prev.invoice_payment_method,
    }));
  };

  if (!canManage) {
    return null;
  }

  const toggleModule = (moduleId, checked) => {
    setForm((prev) => ({
      ...prev,
      requested_modules: { ...prev.requested_modules, [moduleId]: checked },
    }));
    setSubmitError("");
  };

  const scrollToHistory = () => {
    document
      .querySelector('[data-testid="license-request-history-block"]')
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");
    setSubmitSuccess("");
    setRfcError("");
    if (activeBlocked) {
      setSubmitError(LICENSE_REQUEST_MSG_ACTIVE);
      return;
    }
    const rfcTrim = (form.rfc || "").trim();
    if (!rfcTrim) {
      setRfcError(LICENSE_REQUEST_MSG_RFC_REQUIRED);
      setSubmitError(LICENSE_REQUEST_MSG_RFC_REQUIRED);
      return;
    }
    if (legalError || !legalCatalog) {
      setSubmitError(LICENSE_REQUEST_MSG_LEGAL_LOAD_FAILED);
      return;
    }
    const invoiceErr = validateInvoiceFields(form);
    if (invoiceErr) {
      setSubmitError(invoiceErr);
      return;
    }
    const scopeErr = validateScopeFields(form);
    if (scopeErr) {
      setSubmitError(scopeErr);
      return;
    }
    const needsModule = !hasActiveLicense;
    const hasNewModules = requestableModuleIds.some((id) => form.requested_modules?.[id]);
    if (needsModule && !hasAtLeastOneRequestedModule(form.requested_modules, systemInfo, form.commercial_plan)) {
      setSubmitError(LICENSE_REQUEST_MSG_NO_MODULE);
      return;
    }
    if (hasActiveLicense && !hasNewModules && !(form.comments || "").trim()) {
      setSubmitError(
        "Indique en comentarios si solicita renovación, ampliación de vigencia, usuarios o clientes.",
      );
      return;
    }
    if (!canProceedWithRequest) {
      setSubmitError(LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED);
      toast.error(LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED);
      document
        .querySelector('[data-testid="license-commercial-legal-section"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    try {
      let acceptanceId = agreementAcceptanceId;
      if (!useSharedLegal) {
        const acceptancePayload = buildAgreementAcceptancePayload(form, user, {
          activeModuleIds,
          includeActiveForLegal: hasActiveLicense && !hasNewModules,
          catalog,
        });
        const acceptance = await createAgreementAcceptance(acceptancePayload);
        acceptanceId = String(acceptance?.id || "").trim();
      }
      if (!acceptanceId) {
        setSubmitError(LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED);
        return;
      }
      const requestPayload = buildLicenseRequestPayload(form, user, acceptanceId, {
        hasActiveLicense,
        requestableModuleIds,
        catalog,
      });
      delete requestPayload._needsRenewalComment;
      await createLicenseRequest(requestPayload);
      setSubmitSuccess(LICENSE_REQUEST_MSG_SUCCESS);
      if (!useSharedLegal) {
        setLegalAccepted(false);
      }
      const items = await refreshRequests();
      onRequestSubmitted?.();
      const latestPending = (items || []).find((row) => row.status === "pending") || null;
      setLastSubmitted(latestPending);
    } catch (err) {
      setSubmitError(parseLicenseRequestSubmitError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const legalSubmitBlocked = !canProceedWithRequest;
  const submitDisabled =
    activeBlocked
    || submitting
    || legalLoading
    || Boolean(legalError)
    || !legalCatalog
    || !profileBasicsReady;

  const formTitle = hasActiveLicense
    ? "Solicitar renovación o módulo adicional"
    : "Solicitar licencia web";

  return (
    <div className="space-y-4" data-testid="license-request-panel">
      {showSectionHeader ? (
        <div className="space-y-1">
          <h4 className="text-sm font-semibold text-slate-900 m-0">{formTitle}</h4>
          <p className="text-[11px] text-slate-600 leading-snug m-0">
            Envía tu solicitud a BNT por canal web. BNT revisará y, si procede, emitirá o ampliará tu licencia
            sin que pegues token manual.
          </p>
        </div>
      ) : null}

      {activeModuleLabels.length > 0 ? (
        <div
          className="rounded-sm border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-700"
          data-testid="license-request-active-modules"
        >
          <span className="font-semibold">Módulos activos en tu licencia:</span>{" "}
          {activeModuleLabels.join(", ")}
        </div>
      ) : null}

      {allModulesActive ? (
        <p className="text-[11px] text-slate-600 m-0" data-testid="license-request-all-modules-active">
          Tu licencia actual ya tiene todos los módulos principales activos. Puedes solicitar renovación,
          ampliación de vigencia, usuarios o clientes en comentarios.
        </p>
      ) : null}

      {latestApproved && !activeBlocked ? (
        <div
          className="rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-2 text-[11px] text-emerald-950"
          data-testid="license-request-approved-notice"
        >
          {LICENSE_REQUEST_MSG_APPROVED}
        </div>
      ) : null}

      {activeBlocked ? (
        <div
          className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-950"
          data-testid="license-request-active-block"
        >
          {LICENSE_REQUEST_MSG_ACTIVE}
        </div>
      ) : null}

      {legalError ? (
        <div
          className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-950"
          data-testid="license-request-legal-load-error"
        >
          {legalError}
        </div>
      ) : null}

      {submitSuccess ? (
        <div
          className="rounded-sm border border-emerald-200 bg-emerald-50 px-3 py-3 text-[11px] text-emerald-950 space-y-2"
          data-testid="license-request-submit-success"
        >
          <p className="font-semibold m-0">{submitSuccess}</p>
          {lastSubmitted ? (
            <dl className="grid gap-1 m-0">
              <div>
                <dt className="inline font-medium">Fecha: </dt>
                <dd className="inline">{formatDateShort(lastSubmitted.created_at)}</dd>
              </div>
              <div>
                <dt className="inline font-medium">Estado: </dt>
                <dd className="inline">{licenseRequestStatusLabel(lastSubmitted.status)}</dd>
              </div>
              <div>
                <dt className="inline font-medium">Tipo de licencia: </dt>
                <dd className="inline">{licenseTypeLabel(lastSubmitted.requested_license_type)}</dd>
              </div>
              <div>
                <dt className="inline font-medium">Módulos: </dt>
                <dd className="inline">{formatRequestedModulesLabel(lastSubmitted.requested_modules, catalog) || "—"}</dd>
              </div>
              <div>
                <dt className="inline font-medium">Factura: </dt>
                <dd className="inline">{formatRequiresInvoiceLabel(lastSubmitted.requires_invoice)}</dd>
              </div>
            </dl>
          ) : null}
          <div className="flex flex-wrap gap-2 pt-1">
            <Button asChild type="button" size="sm" variant="outline" className="h-8 text-xs">
              <Link to="/" data-testid="license-request-back-panel">
                Volver al panel
              </Link>
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-8 text-xs"
              onClick={scrollToHistory}
              data-testid="license-request-view-history"
            >
              Ver historial
            </Button>
          </div>
        </div>
      ) : null}

      {submitError ? (
        <div
          className="rounded-sm border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-950"
          data-testid="license-request-submit-error"
        >
          {submitError}
        </div>
      ) : null}

      {!profileBasicsReady && !omitSections.profile ? (
        <div
          className="rounded-sm border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-950"
          data-testid="license-request-profile-incomplete"
        >
          Complete RFC y correo de contacto en «Datos del comprador» antes de enviar la solicitud web.
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="space-y-4">
        {!omitSections.profile ? (
        <SectionBlock title="Resumen del comprador" testId="license-request-profile-summary">
          <BuyerProfileSummary
            profile={profileBasicsReady ? profile : form}
            systemInfo={systemInfo}
            showCompleteLink={!profileBasicsReady}
            completeLinkHref="#config-buyer-profile"
          />
        </SectionBlock>
        ) : null}

        {!omitSections.modules && !allModulesActive ? (
          <SectionBlock
            title={hasActiveLicense ? "Módulos a contratar" : "Plan comercial"}
            testId="license-request-modules-section"
          >
            {!hasActiveLicense && commercialPlans.length > 0 ? (
              <div className="space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {commercialPlans.map((plan) => (
                    <label
                      key={plan.id}
                      className={`flex flex-col gap-1 rounded-sm border px-3 py-2 text-[11px] cursor-pointer hover:bg-slate-50 ${
                        form.commercial_plan === plan.id ? "border-[#0F2D5C] bg-slate-50" : "border-slate-200 bg-white"
                      }`}
                      data-testid={`license-request-plan-${plan.id}`}
                    >
                      <span className="flex items-center gap-2 font-semibold text-[#0F2D5C]">
                        <input
                          type="radio"
                          name="commercial_plan"
                          checked={form.commercial_plan === plan.id}
                          onChange={() => setForm((f) => ({ ...f, commercial_plan: plan.id }))}
                          disabled={submitDisabled}
                        />
                        {plan.label || commercialPlanLabel(plan.id)}
                      </span>
                      <span className="text-slate-600 pl-5">
                        Hasta {plan.limits?.authorized_users ?? "—"} usuarios ·{" "}
                        {plan.limits?.authorized_clients ?? "—"} clientes
                      </span>
                    </label>
                  ))}
                </div>
                <div className="max-w-xs">
                  <Label className="text-xs">Cadencia de cobro</Label>
                  <Select
                    value={form.billing_cadence || "annual"}
                    onValueChange={(v) => setForm((f) => ({ ...f, billing_cadence: v }))}
                    disabled={submitDisabled}
                  >
                    <SelectTrigger className="mt-1 h-9 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly" className="text-xs">Mensual</SelectItem>
                      <SelectItem value="annual" className="text-xs">Anual</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {(hasActiveLicense ? requestableModuleIds : requestModuleIds).map((moduleId) => (
                <label
                  key={moduleId}
                  className="flex items-start gap-2 rounded-sm border border-slate-200 bg-white px-3 py-2 text-[11px] cursor-pointer hover:bg-slate-50"
                  data-testid={`license-request-module-${moduleId}`}
                >
                  <Checkbox
                    checked={Boolean(form.requested_modules?.[moduleId])}
                    onCheckedChange={(v) => toggleModule(moduleId, v === true)}
                    disabled={submitDisabled}
                  />
                  <span>{moduleLabel(moduleId, catalog)}</span>
                </label>
              ))}
            </div>
            )}
          </SectionBlock>
        ) : null}

        {!omitSections.licenseType ? (
        <SectionBlock title="Tipo de licencia solicitado" testId="license-request-type-section">
          <div className="max-w-md">
            <Label className="text-xs">Tipo de licencia solicitado</Label>
            <Select
              value={form.requested_license_type || defaultRequestedLicenseType(hasActiveLicense)}
              onValueChange={(v) => setForm((f) => ({ ...f, requested_license_type: v }))}
              disabled={submitDisabled}
            >
              <SelectTrigger className="mt-1 h-9 text-xs" data-testid="license-request-type-select">
                <SelectValue placeholder="Seleccione tipo de licencia" />
              </SelectTrigger>
              <SelectContent>
                {licenseTypeOptions.map((opt) => (
                  <SelectItem key={opt.id} value={opt.id} className="text-xs">
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[10px] text-slate-500 mt-1 m-0">
              BNT confirmará el tipo y la vigencia al autorizar la licencia. Esto no activa módulos por sí solo.
              {hasActiveLicense ? "" : " El registro ya incluye periodo de prueba; puede solicitar contrato anual u otro plazo."}
            </p>
          </div>
        </SectionBlock>
        ) : null}

        {!omitSections.scope ? (
        <SectionBlock title="Alcance estimado" testId="license-request-scope-section">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Usuarios estimados</Label>
              <Input
                type="number"
                min={0}
                value={form.estimated_users}
                onChange={(e) => setForm((f) => ({ ...f, estimated_users: e.target.value }))}
                disabled={submitDisabled}
                className="mt-1 h-9 text-xs"
              />
            </div>
            <div>
              <Label className="text-xs">Clientes estimados</Label>
              <Input
                type="number"
                min={0}
                value={form.estimated_clients}
                onChange={(e) => setForm((f) => ({ ...f, estimated_clients: e.target.value }))}
                disabled={submitDisabled}
                className="mt-1 h-9 text-xs"
              />
            </div>
          </div>
          <div className="mt-2">
            <Label className="text-xs">Comentarios {hasActiveLicense ? "(indique renovación o ampliación)" : "(opcional)"}</Label>
            <SpellcheckTextarea
              value={form.comments}
              onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
              disabled={submitDisabled}
              rows={3}
              className="mt-1 rounded-sm text-xs resize-none"
              testId="license-request-comments"
            />
          </div>
        </SectionBlock>
        ) : null}

        {!omitSections.invoice ? (
        <SectionBlock title="Factura" testId="license-request-invoice-section">
          <LicenseInvoiceSection
            form={form}
            setForm={setForm}
            profile={profile}
            disabled={submitDisabled}
            onApplyRegistrationData={applyRegistrationInvoiceData}
            showModuleSummary
            testId="license-request-invoice-fields"
          />
        </SectionBlock>
        ) : null}

        {!omitSections.paymentProof ? (
        <LicensePaymentProofSection
          form={form}
          setForm={setForm}
          disabled={submitDisabled}
          testId="license-request-payment-proof"
        />
        ) : null}

        {!omitSections.legal ? (
        <LegalAcceptanceCheckbox
          checked={legalLocked || legalAccepted}
          onCheckedChange={(v) => {
            setLegalAccepted(v);
            setSubmitError("");
          }}
          disabled={submitDisabled}
          locked={legalLocked}
          contractVersion={legalCatalog?.contract_version}
          documents={legalCatalog?.documents || []}
        />
        ) : null}

        <Button
          type="submit"
          size="sm"
          disabled={submitDisabled}
          className={`h-9 text-xs font-semibold bg-[#0F2D5C] hover:bg-[#0a1f42] ${
            legalSubmitBlocked && !submitDisabled ? "opacity-70" : ""
          }`}
          data-testid="license-request-submit"
        >
          {submitting ? (
            <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
          ) : (
            <Send className="w-3.5 h-3.5 mr-1.5" />
          )}
          Enviar solicitud web
        </Button>
      </form>

      {showHistory ? <LicenseRequestHistory /> : null}
    </div>
  );
}
